import React, { useRef } from 'react';
import styled from 'styled-components';
import { useDebounce } from 'use-debounce';

import { useSearchUsersQuery } from 'api';
import CardList from 'components/Card/CardList';
import CommaList from 'components/CommaList';
import EntityElement from 'components/EntityElement';
import Flex from 'components/Flex';
import TextHeader from 'components/TextHeader';
import { COLORS } from 'config/constants';
import Card from 'components/Card';
import { V2ProUser } from 'types';
import { FormValues } from './ArtistMembers';
import { QueryStatus } from '@reduxjs/toolkit/dist/query';

const EntityTitleMeta = styled.span`
  // TODO: this is the standard body styling from _globals.scss.. should we store this somewhere?
  font-size: 1.4rem;
  font-weight: 400;
  font-style: italic;
  color: ${COLORS.GRAY_MEDIUM};
`;

type UserData = FormValues | V2ProUser;

interface ArtistMemberLookupProps {
  selectedMemberId?: number;
  values: FormValues;
  emailValid: boolean;
  setSelectedUser: (user: UserData) => void;
}

export const ArtistMemberLookup: React.FC<ArtistMemberLookupProps> = ({
  selectedMemberId,
  values,
  emailValid,
  setSelectedUser
}) => {
  // We want to lock the query arguments as the cached form values when a user is selected from the matching users
  // list, but we still want to set the selected user data so that the form data is updated
  const formValuesCache = useRef(values); // save off form values if selecting user from list
  const [{ first_name, last_name }] = useDebounce(values, 300);
  const pro_email = emailValid ? values.pro_email : undefined;
  const queryArgs = selectedMemberId
    ? formValuesCache.current
    : {
        pro_email,
        first_name,
        last_name,
        country: values.country
      };

  const skipMemberFetch =
    (!pro_email && !(first_name && last_name)) || !!selectedMemberId;

  const {
    data: matchingUsersData,
    isFetching: isFetchingUsers,
    status
  } = useSearchUsersQuery(queryArgs, {
    skip: skipMemberFetch
  });

  // matchingUsersData is undefined when skip is true. Not sure how/when this changed, but here we are.
  const matchingUsersRef = useRef(matchingUsersData);

  // don't update ref if a user is selected, but if skip is true and there is no selection then clear values
  if (!selectedMemberId) {
    matchingUsersRef.current =
      status === QueryStatus.uninitialized ? [] : matchingUsersData;
  }

  const matchingUsers = matchingUsersRef.current;
  const showUserResults =
    matchingUsers && matchingUsers instanceof Array && matchingUsers.length > 0;

  if (!showUserResults) return null;

  return (
    <div className="mt-2 mb-4">
      <TextHeader type={TextHeader.TYPES.MEDIUM} className="mb-0">
        Existing User Accounts
      </TextHeader>
      <p>
        Do any of these users look like the same person you’re adding? If so, select and
        add them to your artist without creating a duplicate account.
      </p>

      <CardList className="mt-2" style={{ opacity: isFetchingUsers ? 0.8 : undefined }}>
        {matchingUsers?.map(user => {
          const isSelected = selectedMemberId === user.id;

          return (
            <CardList.Item
              active={isSelected}
              data-testid={ARTIST_MEMBER_LOOKUP_TEST_IDS.USER_ROW}
              key={user.id}
              onClick={() => {
                // if we haven't selected a user, store the current form values before setting the user data
                // into the form
                if (!selectedMemberId) formValuesCache.current = values;

                const userData: UserData = isSelected ? formValuesCache.current : user;
                setSelectedUser(userData);
              }}
            >
              <Flex style={{ width: '100%', minHeight: '3.9rem' }}>
                <EntityElement
                  size={EntityElement.SIZES.SMALL}
                  type={EntityElement.TYPES.USER}
                  data={user}
                  title={
                    <>
                      <span>
                        {user.first_name} {user.last_name}
                      </span>

                      {user.artists && user.artists.length > 0 && (
                        <EntityTitleMeta>
                          {' '}
                          (
                          <CommaList
                            data={user.artists}
                            attribute="name"
                            limit={4}
                            title={user.artists?.length > 4}
                          />
                          )
                        </EntityTitleMeta>
                      )}
                    </>
                  }
                />
              </Flex>
            </CardList.Item>
          );
        })}
      </CardList>

      <Card
        shadow={false}
        fitHeight
        className="mt-2 mb-1"
        onClick={() => {
          if (!selectedMemberId) return;
          const userData = formValuesCache.current;
          setSelectedUser(userData);
        }}
        isActive={!selectedMemberId}
        data-testid={ARTIST_MEMBER_LOOKUP_TEST_IDS.CREATE_NEW_USER_BUTTON}
      >
        <Card.Content>
          <TextHeader type={TextHeader.TYPES.MEDIUM}>
            None of these accounts match?
          </TextHeader>
          Select this option and we&apos;ll set up a new user.
        </Card.Content>
      </Card>
    </div>
  );
};

export const ARTIST_MEMBER_LOOKUP_TEST_IDS = {
  USER_ROW: 'ArtistMemberLookup-user',
  CREATE_NEW_USER_BUTTON: 'ArtistMemberLookup-create-new-user'
} as const;
